import { PRMCodes, WHEELCHAIR_PRIORITY } from "_utils/constants";
import { format } from "date-fns";
import moment from "moment";

function groupBookingsByDayAndHour(bookings, airport, isTask) {
    const result = {};

    bookings.forEach(booking => {
        //TODO, review time property.
        const timeField = isTask ? booking.flightInformation.flightTime :
            booking.flightConfirmationDetails.destination === airport
                ? booking.flightConfirmationDetails.arrivalTime
                : booking.flightConfirmationDetails.departureTime;

        if (!timeField) return; // Skip if no time available

        // Parse the datetime
        const dateTime = new Date(timeField);
        const dateKey = dateTime.toISOString().split('T')[0]; // YYYY-MM-DD

        const hour = dateTime.getHours();
        // Initialize day if not exists
        if (!result[dateKey]) {
            result[dateKey] = {
                total: 0,
                hours: {}
            };

            // Initialize all hour slots
            for (let h = 0; h < 24; h++) {
                const hourKey = `${h.toString().padStart(2, '0')}:00`;
                result[dateKey].hours[hourKey] = 0;
            }
        }

        const hourKey = `${hour.toString().padStart(2, '0')}:00`;
        result[dateKey].hours[hourKey]++;
        result[dateKey].total++;
    });

    return result;
}

//This function will return data for staff and break task staff
function transformHourlyStaffData(staffSectionData, isBreak) {
    const result = {};

    const initHourMap = () => {
        const map = {};
        for (let h = 0; h < 24; h++) {
            const hourKey = `${h.toString().padStart(2, '0')}:00`;
            map[hourKey] = 0;
        }
        return map;
    };

    const convertToLocalHour = (day, hourStr) => {
        const utcDate = new Date(`${day}T${hourStr}:00Z`); // UTC timestamp
        const localHour = utcDate.getHours(); // Local hour in system timezone
        return `${localHour.toString().padStart(2, "0")}:00`;
    };

    for (const item of staffSectionData) {
        const day = item.day;

        if (!result[day]) {
            result[day] = {
                total: 0,
                hours: initHourMap(),
            };
        }

        const updateHourCount = (hourArray) => {
            for (const hour of hourArray) {
                const localHour = convertToLocalHour(day, hour);
                result[day].hours[localHour] = (result[day].hours[localHour] || 0) + 1;
                result[day].total += 1;
            }
        };

        if (!isBreak && Array.isArray(item.hours)) {
            updateHourCount(item.hours);
        } else if (isBreak && Array.isArray(item.breakTaskInfo)) {
            for (const breakObj of item.breakTaskInfo) {
                if (Array.isArray(breakObj.hours)) {
                    updateHourCount(breakObj.hours);
                }
            }
        }
    }

    return result;
}

function calculateShortfall(staffData, prmBookings, boardingDocs) {
    const result = {};

    const allDays = new Set([
        ...Object.keys(staffData),
        ...Object.keys(prmBookings),
        ...Object.keys(boardingDocs),

    ]);

    for (const day of allDays) {
        const staffDay = staffData[day]?.hours || {};
        const prmDay = prmBookings[day]?.hours || {};
        const boardingDay = boardingDocs[day]?.hours || {};


        const shortfallPerHour = {};
        for (let h = 0; h < 24; h++) {
            const hourKey = `${h.toString().padStart(2, '0')}:00`;
            const staffCount = staffDay[hourKey] || 0;
            const prmCount = prmDay[hourKey] || 0;
            const boardingCount = boardingDay[hourKey] || 0;

            shortfallPerHour[hourKey] = (prmCount + boardingCount) - staffCount;
        }

        result[day] = shortfallPerHour;
    }

    return result;
}


async function groupBookingsByFlightsAndHour(bookings, airport) {
    const grouped = {
        arrival: {},
        departure: {}
    };

    for (const booking of bookings) {
        const details = booking.flightConfirmationDetails;
        if (!details) return;

        const {
            carrierCode,
            flightNumber,
            origin,
            destination,
            departureTime,
            arrivalTime,
        } = details;

        let type = "";
        let timeStr = "";

        if (destination === airport) {
            type = "arrival";
            timeStr = arrivalTime;
        } else if (origin === airport) {
            type = "departure";
            timeStr = departureTime;
        } else {
            return; // Skip if unrelated to airport
        }

        if (!timeStr) continue; // Skip if no valid time
        const dateObj = new Date(timeStr);
        if (isNaN(dateObj.getTime())) {
            console.warn("Invalid time format for booking:", booking._id, timeStr);
            continue;
        }

        const day = dateObj.getFullYear() + "-" +
            String(dateObj.getMonth() + 1).padStart(2, "0") + "-" +
            String(dateObj.getDate()).padStart(2, "0");
        const hour = dateObj.getHours().toString().padStart(2, "0") + ":00";
        const flightCode = `${carrierCode}${flightNumber}`;
        // Initialize structure if needed
        if (!grouped[type][day]) grouped[type][day] = {};
        if (!grouped[type][day][hour]) grouped[type][day][hour] = {};
        if (!grouped[type][day][hour][flightCode]) grouped[type][day][hour][flightCode] = 0;

        // Increment flight count
        grouped[type][day][hour][flightCode]++;
    };

    return grouped;
}

export const generateComparisonReportData = async (props) => {
    try {
        const { airport, boardingTasksDocs, prmBookingsData, staffSectionData } = props;
        const prmBookings = groupBookingsByDayAndHour(prmBookingsData, airport, false);
        const boardingDocs = groupBookingsByDayAndHour(boardingTasksDocs, airport, true);
        // const departureDocs = groupBookingsByDayAndHour(departureTaskDocs, airport, true);//TODO,review it more with Ghader
        const staffData = transformHourlyStaffData(staffSectionData, false);
        const staffBreak = transformHourlyStaffData(staffSectionData, true)
        const shortfall = calculateShortfall(staffData, prmBookings, boardingDocs)
        const { departure, arrival } = await groupBookingsByFlightsAndHour(prmBookingsData, airport);

        return {
            "staff vs prm": {
                staffData,
                staffBreak,
                prmBookings,
                boardingDocs,
                // departureDocs,
                shortfall
            },
            "shortfall map": shortfall,
            "flights": { flightsDocsInbound: arrival, flightsDocsOutbound: departure }
        }
    } catch (error) {
        console.error(error, "Error,,,")
        throw new Error("Error generate report data" + error.message)
    }
}


export const processConversationsMessagesReportData = (data, type) => {
    try {
        if (!Array.isArray(data)) {
            throw new Error("Data must be an array");
        }

        // format the data for Excel export
        const formattedData = data.map(message => {
            // convert Firestore timestamp to readable date
            let sentAt = "";
            if (message.sentAt) {
                if (message.sentAt._seconds) {
                    // firestore timestamp
                    const date = new Date(message.sentAt._seconds * 1000);
                    sentAt = date.toLocaleString();
                } else if (message.sentAt instanceof Date) {
                    sentAt = message.sentAt.toLocaleString();
                } else {
                    sentAt = new Date(message.sentAt).toLocaleString();
                }
            }

            const finalObject = {
                "Sender Name": message.senderName || "N/A",
                "Sender ID": message.senderId || "N/A",
                "Sent At": sentAt,
                "Message": message.text || "N/A",
                "Source": message?.source || message?.senderType?.toUpperCase() || "N/A",
            };
            if (type === "dispatcherWithEmployee") {
                finalObject["Broadcast"] = message?.isBroadcastMessage ? "Yes" : "No";
                finalObject["Area"] = message?.area ? message?.area?.name : "N/A";
                finalObject["Description"] = message?.description || "N/A";
            }
            return finalObject;
        });

        // calculate statistics
        const totalMessages = data.length;
        const senderCounts = {};

        data.forEach(message => {
            const senderId = message.senderId;
            if (senderId) {
                senderCounts[senderId] = (senderCounts[senderId] || 0) + 1;
            }
        });

        // create summary data
        const summaryData = [
            { "Metric": "Total Messages", "Value": totalMessages },
            { "Metric": "", "Value": "" }, // empty row for spacing
        ];

        // add sender counts
        Object.entries(senderCounts).forEach(([senderId, count]) => {
            const senderName = data.find(msg => msg.senderId === senderId)?.senderName || senderId;
            summaryData.push({
                "Metric": `Messages by ${senderName}`,
                "Value": count
            });
        });

        let titles = ["Sender Name", "Sender ID", "Sent At", "Message", "Source"];
        if (type === "dispatcherWithEmployee") {
            titles.push("Broadcast", "Area", "Description");
        }
        return {
            summary: summaryData,
            details: formattedData,
            titles: titles
        };

    } catch (error) {
        console.error(error, "Error processing employee conversation report data");
        throw new Error("Error processing employee conversation report data: " + error.message);
    }
};

export const chatsMessagesFileHandler = (fileObj, setExcelRows, setExcelColumns, airportLogo, type) => {
    if (fileObj && typeof fileObj === "object" && fileObj.summary && fileObj.details) {
        // handle processed employee conversation data directly
        const logoRow = [
            {
                value: (
                    <div style={{ textAlign: "center" }}>
                        <img
                            src={airportLogo}
                            alt="Airport Logo"
                            style={{ width: "150px", height: "auto" }}
                        />
                    </div>
                ),
                colspan: fileObj.titles?.length ?? 7, // use titles length for colspan
            },
        ];

        // create summary rows for display
        const summaryRows = fileObj.summary.map(item => [item.Metric, item.Value]);

        // create details rows with headers
        const detailsRows = [
            fileObj.titles, // header row
            ...fileObj.details.map(item => [
                item["Sender Name"],
                item["Sender ID"],
                item["Sent At"],
                item["Message"],
                item["Source"],
                (type === "dispatcherWithEmployee" ? item["Broadcast"] : ""),
                (type === "dispatcherWithEmployee" ? item["Area"] : ""),
                (type === "dispatcherWithEmployee" ? item["Description"] : ""),

            ])
        ];

        // combine all rows
        const allRows = [logoRow, ...summaryRows, [""], ...detailsRows]; // Empty row for spacing

        setExcelRows(allRows);
        setExcelColumns(fileObj.titles.map((title, index) => ({ name: String.fromCharCode(65 + index), key: index })));
    }
}

//#region helper functions
export const getTaskType = (task) => {
    if (task?._id?.includes("selfAllocatedTask")) return "Self allocated";
    if (task?.combinedBooking) return "Boarding";
    if (task?.isTransitBooking) return "Transit";
    if (task?.isALTPickUp) return "Alt Pickup";
    if (task?.combinedRampBooking) return "Ramp";
    if (task?.flightTask) return "flight Task";
    if (task?.groupedBooking) return "Grouped Self allocated";
    if (task?.transferredFromTaskId) return "Transferred";

    return task?.flightBooking ? "Arrival" : "Departure";
};

function normalizePreadvised(val) {
    if (val === undefined || val === null) return "unknown";
    const s = String(val).trim().toUpperCase();
    if (!s) return "unknown";
    if (s === "Y" || s === "YES" || s === "TRUE" || s === "1" || s.startsWith("Y")) return "yes";
    if (s === "N" || s === "NO" || s === "FALSE" || s === "0" || s.startsWith("N")) return "no";
    return "unknown";
}

//get one assistance or one with high priority as same order of array
function pickBillableCode(validCodes) {
    if (!validCodes.length) return null;

    const hasWheel = validCodes.some(c => WHEELCHAIR_PRIORITY.includes(c));
    if (hasWheel) {
        for (const code of WHEELCHAIR_PRIORITY) {
            if (validCodes.includes(code)) return code;
        }
    }

    // No wheelchair code present: pick the first valid code
    return validCodes[0];
}

// Slightly hardened extractor — returns unique, ordered known codes
function extractValidCodesForBreakDown(raw) {
    if (!raw && raw !== 0) return [];
    // Ensure PRMCodes exists; fallback to an empty array if undefined
    const VALID_SET = new Set(Array.isArray(PRMCodes) ? PRMCodes.map(c => String(c).toUpperCase()) : []);

    // Join arrays to a single string; otherwise cast to string.
    const text = Array.isArray(raw) ? raw.join(" ") : String(raw || "");
    const upper = text.toUpperCase();

    // Build regex from VALID_SET if possible; fall back to a broad pattern.
    const codesFromSet = [...VALID_SET];
    let pattern;
    if (codesFromSet.length) {
        // escape codes and join
        const alt = codesFromSet.map(c => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
        pattern = new RegExp(`\\b(${alt})\\b`, "g");
    } else {
        // fallback pattern (keeps your original list if PRMCodes not defined)
        pattern = /\b(WCHC|WCHS|WCHR|BLND|MEDA|DEAF|DPNA|SVAN|LNYD|WCMP|WCLB|WCHP|WCBW|WCBD|STCR|PETC|PPOC|ESAN|OXYG)\b/g;
    }

    const matches = upper.match(pattern) || [];

    // Deduplicate while preserving order, and ensure they are in VALID_SET (if present)
    const seen = new Set();
    const unique = [];
    for (const m of matches) {
        if (!seen.has(m) && (VALID_SET.size === 0 || VALID_SET.has(m))) {
            seen.add(m);
            unique.push(m);
        }
    }
    return unique;
}

function extractPrmsFromRow(row) {

    const codes = [];
    if (!Array.isArray(row)) return codes
    for (const cell of row) {
        if (typeof cell !== "string") continue;
        const cellContent = cell.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
        for (const content of cellContent) if (PRMCodes.includes(content)) codes.push(content);
    }
    const preadvised = normalizePreadvised(row[7]);


    // one count per code per row; remove Set() if you want duplicates in same row to count multiple times
    return { codes, preadvised }
}



// Count PRM codes across all data rows (skip header at index 0)
const countPrmCodes = (rows, isPassengerRecordsReport) => {
    const counts = Object.fromEntries(PRMCodes.map(c => [c, 0]));

    for (let i = 1; i < rows.length; i++) {
        let { codes, preadvised } = extractPrmsFromRow(rows[i]);
        // const advisedPairs= extraxtPreAdvisedPairs()
        if (isPassengerRecordsReport) {
            // pick the single billable code (based on wheelchair priority)
            const validCodes = extractValidCodesForBreakDown(codes);
            const chosen = pickBillableCode(validCodes || []);
            if (!chosen) continue;

            // ensure key exists
            if (!counts[chosen]) counts[chosen] = { total: 0, advised: { yes: 0, no: 0, unknown: 0 } };

            counts[chosen].total += 1;
            counts[chosen].advised[preadvised] = (counts[chosen].advised[preadvised] || 0) + 1;
        } else {
            for (const code of codes) counts[code]++;

        }
    }
    return counts;
};

//Helper functions for fileHandler
export const addHeadersIntoReport = (resp, isPassengerRecordsReport) => {

    const headerRows = [];
    const colspan = resp.cols?.length ?? 10;


    // get count of. each assistance (prm code)
    const counts = countPrmCodes(resp.rows, isPassengerRecordsReport);

    if (isPassengerRecordsReport) {
        const codes = Object.entries(counts).filter(([, n]) => typeof n === "object");

        if (codes.length > 0) {
            headerRows.push([
                {
                    value: <div style={{ fontWeight: "bold", textAlign: "center" }}>PRM assistance totals</div>,
                    colspan,
                },
            ]);
            for (const [code, n] of codes) {
                headerRows.push([
                    {
                        value: (
                            <div style={{ display: "flex", justifyContent: "flex-start" }}>
                                <span style={{ fontWeight: 400 }}>{code}:{`${n.total} / Preadvised: ${n.advised.yes} / NonPreadvised: ${n.advised.no}`}</span>
                            </div>
                        ),
                        colspan,
                    },
                ]);
            }

        }


    } else {
        const codes = Object.entries(counts).filter(([, n]) => n > 0);

        if (codes.length > 0) {
            // Title row
            headerRows.push([
                {
                    value: <div style={{ fontWeight: "bold", textAlign: "center" }}>PRM assistance totals</div>,
                    colspan,
                },
            ]);

            // One row per code
            for (const [code, n] of codes) {
                headerRows.push([
                    {
                        value: (
                            <div style={{ display: "flex", justifyContent: "flex-start" }}>
                                <span style={{ fontWeight: 400 }}>{code}:{n}</span>
                            </div>
                        ),
                        colspan,
                    },
                ]);
            }
        }
    }

    headerRows.unshift([
        {
            value: `Total Passengers: ${Math.max(0, resp.rows.length - 1)}`, // skip header row
            colspan,
            style: { fontWeight: "bold", textAlign: "center" },
        },
    ]);


    return headerRows;
};

// add next character to the excel columns due to the missing character at the last item 
export const addNextObject = (arr) => {
    if (!Array.isFullArray(arr)) return [];

    // Get the last object in the array
    const lastObject = arr[arr.length - 1];

    // Get the next character after the last object's name
    const nextChar = String.fromCharCode(lastObject.name.charCodeAt(0) + 1);

    // Get the next key
    const nextKey = lastObject.key + 1;

    // Add the new object to the array
    const newObject = { name: nextChar, key: nextKey };

    // Push the new object to the array
    arr.push(newObject);

    // Return the updated array
    return arr;
};

export const processReviewsData = (data, iata) => {
    return data.map((review) => {
        const type = review.arrival.details.iata === iata ? "arrival" : "departure"
        return {
            name: review?.name ? review.name : 'N/A\t\t',
            flightDate: format((new Date(review.flight.details.departureDate)), "yyyy-MM-dd"),
            flightNumber: review.flight.details.carrierCode + review.flight.details.flightNumber,
            assistanceQuality: review[type].review.quality,
            waitingTime: review[type].review.waitingTime,
            staffAssistance: review[type].review.staff,
            comments: review[type].review.comments,
        }
    })
}

const toYMD = (d) => moment(d).format("YYYY-MM-DD");

export const getRangeYMD = (range) => {
    const [start, end] = range || [];
    const s = toYMD(start || new Date());
    const e = toYMD(end || start || new Date());
    return { startDate: s, endDate: e };
};

export const isValidDate = (value) => {
    if (!value) return false;
    const d = new Date(value);
    return !isNaN(d.getTime());
};
